import type { ItemVariantPickerDto } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';

/**
 * PD-16509 — the product page's variant picker: one choice per variant of the
 * item's product that this customer can buy (strength, count, size...).
 * Choosing one opens that variant's own product page, since each variant has
 * its own price, stock and item number. The server already resolved a shared
 * (master) item to this storefront company's own item and dropped every
 * variant this customer cannot see.
 */
export function VariantPicker({
	picker,
	onSelect,
}: {
	picker: ItemVariantPickerDto;
	onSelect: (itemId: number) => void;
}): JSX.Element {
	const { t } = useTranslation();

	return (
		<div className="mb-4" data-testid="variant-picker">
			<p className="text-sm font-medium text-default mb-2">
				{t('Choose an option')}
			</p>
			<div className="flex flex-wrap gap-2" role="radiogroup">
				{picker.options.map((option) => (
					<button
						key={option.itemId}
						type="button"
						role="radio"
						aria-checked={option.isCurrent}
						onClick={() => {
							if (!option.isCurrent) {
								onSelect(option.itemId);
							}
						}}
						className={`min-h-touch px-3 py-2 rounded-lg border text-sm text-left transition-colors ${
							option.isCurrent
								? 'border-brand-primary-fg bg-brand-primary text-brand-primary-fg font-medium'
								: 'border-brand-border bg-brand-input text-default hover:bg-brand-surface'
						}`}
					>
						{t(option.label)}
					</button>
				))}
			</div>
		</div>
	);
}
